/**
 * Timezone helpers for the transplanted screens. Ported verbatim from the real Calls screen's
 * night-boundary math (noon-to-noon rule) so "Tonight" means the same instant here as it does
 * on the old console, plus small formatters for the tenant's zone. Every timestamp the console
 * renders goes through the tenant's IANA zone (useTenantZone), never the operator's browser zone.
 */

export function tzOffsetMs(at: Date, tz: string | undefined): number {
  if (!tz) return -at.getTimezoneOffset() * 60_000;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(at);
    const pick = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? "0");
    const hour = pick("hour") === 24 ? 0 : pick("hour");
    const asUtc = Date.UTC(pick("year"), pick("month") - 1, pick("day"), hour, pick("minute"), pick("second"));
    return asUtc - (at.getTime() - at.getMilliseconds());
  } catch {
    return -at.getTimezoneOffset() * 60_000;
  }
}

/** The instant a night began, `nightsBack` nights ago, on the noon-to-noon rule. */
export function nightStartIso(nowMs: number, tz: string | undefined, nightsBack: number): string {
  const now = new Date(nowMs);
  const offset = tzOffsetMs(now, tz);
  const local = new Date(nowMs + offset);
  const rollback = (local.getUTCHours() < 12 ? 1 : 0) + nightsBack;
  const wallNoon = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() - rollback, 12, 0, 0);
  return new Date(wallNoon - offset).toISOString();
}

/** Clock time (e.g. "9:41 PM") in the tenant's zone. */
export function clock(iso: string, tz: string | undefined): string {
  try {
    return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

/** Short date + time (e.g. "Sep 4, 9:41 PM") in the tenant's zone. */
export function stamp(iso: string, tz: string | undefined): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** "3m 12s" from seconds. */
export function secs(total: number | null): string {
  if (total === null || total === undefined) return "—";
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

/** Date only (e.g. "Sep 4, 2026") in the given zone (default browser). */
export function stampDate(iso: string, tz?: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: tz }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}
